import React from 'react';
import { AppShell, Group, Text, Button, Switch, useMantineColorScheme } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  IconLogout,
  IconSun,
  IconMoon
} from '@tabler/icons-react';

const MemberLayout = ({ children }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();

  const handleLogout = async () => {
    await logout();
    navigate('/member/login');
  };

  return (
    <AppShell
      header={{ height: { base: 120, sm: 100 } }}
      px={{ base: "sm", sm: 300 }}    // padding left/right
      py={{ base: "sm", sm: "md" }}     // padding top/bottom
    >
      <AppShell.Header p="md">
        {/* Mobile Layout */}
        <div
          style={{
            display: 'block',
            padding: '2px 1px',
            backgroundColor: colorScheme === 'dark' ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-white)',
          }}
          className="sm:hidden"
        >
          {/* Logo and Title Section */}
          <div style={{
            textAlign: 'center',
            marginBottom: '8px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1px',
            flexWrap: 'wrap'
          }}>
            <img
              src="/logo512.png"
              width="32px"
              height="32px"
              style={{
                flexShrink: 0,
                borderRadius: '6px',
              }}
              alt="Manifestation Magic Logo"
            />
            <Text
              size="20px"
              fw={700}
              style={{
                fontFamily: '"Playfair Display", serif',
                background: 'linear-gradient(135deg, #228be6 0%, #7c3aed 50%, #e64980 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '-0.3px',
                lineHeight: 1.2,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0
              }}
            >
              21 Day Manifestation Magic
            </Text>
          </div>

          {/* Controls Section */}
          <Group
            justify="space-between"
            align="center"
            style={{
              paddingTop: '4px'
            }}
          >
            <Switch
              size="md"
              color="violet.6"
              onLabel={<IconSun size={15} stroke={2.5} />}
              offLabel={<IconMoon size={15} stroke={2.5} />}
              checked={colorScheme === 'dark'}
              onChange={() => toggleColorScheme()}
              styles={{
                track: {
                  border: colorScheme === 'dark' 
                    ? '2px solid var(--mantine-color-dark-4)' 
                    : '2px solid #e9ecef'
                }
              }}
            />
            <Button
              variant="light"
              color="red"
              size="sm"
              leftSection={<IconLogout size={16} stroke={2} />}
              onClick={handleLogout}
              styles={{
                root: {
                  fontWeight: 600,
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)'
                  }
                }
              }}
            >
              Logout
            </Button>
          </Group>
        </div>

        {/* Desktop Layout */}
        <Group
          h="80px"
          justify="space-between"
          align="center"
          style={{
            display: 'none',
            backgroundColor: colorScheme === 'dark' ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-white)',
            marginLeft: '40px',
          }}
          className="sm:flex"
        >
          {/* Left side - Logo and Title */}
          <Group align="center" gap="md">
            <img
              src="/logo512.png"
              width="55px"
              height="55px"
              style={{
                borderRadius: '8px'
              }}
              alt="Manifestation Magic Logo"
            />
            <Text
              size="32px"
              fw={700}
              style={{
                fontFamily: '"Playfair Display", serif',
                background: 'linear-gradient(135deg, #228be6 0%, #7c3aed 50%, #e64980 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '-0.5px',
                lineHeight: 1.2
              }}
            >
              21 Day Manifestation Magic
            </Text>
          </Group>

          {/* Right side - Controls */}
          <Group align="center" gap="lg">
            <Switch
              size="md"
              color="violet.6"
              onLabel={<IconSun size={16} stroke={2.5} />}
              offLabel={<IconMoon size={16} stroke={2.5} />}
              checked={colorScheme === 'dark'}
              onChange={() => toggleColorScheme()}
              styles={{
                track: {
                  border: colorScheme === 'dark' 
                    ? '2px solid var(--mantine-color-dark-4)' 
                    : '2px solid #e9ecef'
                }
              }}
            />
            <Button
              variant="light"
              color="red"
              size="md"
              leftSection={<IconLogout size={18} stroke={2} />}
              onClick={handleLogout}
              styles={{
                root: {
                  fontWeight: 600,
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)'
                  }
                }
              }}
            >
              Logout
            </Button>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Main>
        {children}
      </AppShell.Main>
    </AppShell>
  );
};

export default MemberLayout;